
import { supabase } from '@/integrations/supabase/client';
import { User } from '@/types/user';
import { toast } from '@/hooks/use-toast';

export const useUserCredit = (
  user: User | null,
  setUser: React.Dispatch<React.SetStateAction<User | null>>
) => {
  const addUserCredit = async (email: string, amount: number): Promise<boolean> => {
    try {
      const { data: userData, error: userError } = await supabase.rpc('get_user_by_email', {
        email_to_search: email
      });

      if (userError || !userData) {
        toast({
          title: 'User Not Found',
          description: 'No user found with that email address.',
          variant: 'destructive',
        });
        return false;
      }

      const userId = typeof userData === 'object' && userData !== null && 'id' in userData ? 
                     String(userData.id) : null;

      if (!userId) {
        toast({
          title: 'Error',
          description: 'Could not retrieve user information.',
          variant: 'destructive',
        });
        return false;
      }

      // Use the record_credit_transaction function which updates profiles table
      const { error: transactionError } = await supabase.rpc('record_credit_transaction', {
        p_user_id: userId,
        p_amount: amount,
        p_transaction_type: 'admin_credit',
        p_description: `Admin added $${amount} credit`
      });

      if (transactionError) {
        throw transactionError;
      }

      // Update local user state if it's the same user
      if (user && user.id === userId) {
        setUser({
          ...user,
          credit: (user.credit || 0) + amount
        });
      }

      toast({
        title: 'Credit Added',
        description: `$${amount} credit has been added to ${email}'s account.`,
      });
      return true;
    } catch (error: any) {
      console.error('Failed to add credit:', error);
      toast({
        title: 'Failed to Add Credit',
        description: error.message || 'Please try again.',
        variant: 'destructive',
      });
      return false;
    }
  };

  const useCredit = async (amount: number): Promise<void> => {
    if (!user) {
      toast({
        title: 'Not Authenticated',
        description: 'You must be logged in to use credit.',
        variant: 'destructive',
      });
      return;
    }

    if (amount <= 0) {
      toast({
        title: 'Invalid Amount',
        description: 'Please enter a valid amount.',
        variant: 'destructive',
      });
      return;
    }

    if ((user.credit || 0) < amount) {
      toast({
        title: 'Insufficient Credit',
        description: 'You do not have enough credit to use this amount.',
        variant: 'destructive',
      });
      return;
    }

    try {
      // Use the record_credit_transaction function which updates profiles table
      const { error: transactionError } = await supabase.rpc('record_credit_transaction', {
        p_user_id: user.id,
        p_amount: -amount,
        p_transaction_type: 'purchase_credit',
        p_description: `Used $${amount} credit for purchase`
      });

      if (transactionError) {
        throw transactionError;
      }

      // Update local user state
      setUser({
        ...user,
        credit: (user.credit || 0) - amount
      });

      toast({
        title: 'Credit Applied',
        description: `$${amount} credit has been applied to your purchase.`,
      });
    } catch (error: any) {
      console.error('Failed to use credit:', error);
      toast({
        title: 'Failed to Use Credit',
        description: error.message || 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  return { addUserCredit, useCredit };
};
